import { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Card,
  message,
  Skeleton,
  Tabs,
  Flex,
  Typography,
  List,
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  EuroCircleOutlined,
  FileOutlined,
  InfoCircleOutlined,
  MessageOutlined,
  PhoneOutlined,
  UserOutlined,
} from '@ant-design/icons';
import PageScaffold from '../../components/PageScaffold';
import { COMPANIES_PAGE_PATH } from './CompaniesPage';
import CompanyCreationForm from '../../components/companies_page/CompanyCreationForm';
import CompanyDetails from '../../components/companies_page/CompanyDetails';
import { CompanyCreationFormProvider } from '../../contexts/data/companies/CompanyCreationFormContext';
import { CompanyEditionFormProvider } from '../../contexts/data/companies/CompanyEditionFormContext';
import CompaniesContext from '../../contexts/data/companies/CompaniesContext';
import { ContextStatus, SubscriptionStatus } from '@leedpulse-monorepo/enums';
import CompanyUsersList from './CompanyUsersList';
import CompanySubscriptionsList from './CompanySubscriptionsList';
import CompanyCosts from './CompanyCosts';
import CompanyFilesList from '../../components/companies_page/CompanyFilesList';
import { CompanyFilesProvider } from '../../contexts/data/companies/CompanyFilesContext';
interface CompanyDetailPageProps {
  isCreateMode: boolean;
}

const TABS = {
  generalInformation: {
    key: '1',
    label: 'Informations générales',
    icon: <InfoCircleOutlined />,
  },
  users: {
    key: '2',
    label: 'Utilisateurs',
    icon: <UserOutlined />,
  },
  subscriptions: {
    key: '3',
    label: 'Abonnements',
    icon: <CalendarOutlined />,
  },
  numbers: {
    key: '4',
    label: 'Numéros',
    icon: <PhoneOutlined />,
  },
  sms: {
    key: '5',
    label: 'SMS',
    icon: <MessageOutlined />,
  },
  costs: {
    key: '6',
    label: 'Coûts',
    icon: <EuroCircleOutlined />,
  },
  files: {
    // Cle lisible : la page globale des documents ouvre un fichier via ?tab=files&file=<id>
    key: 'files',
    label: 'Fichiers',
    icon: <FileOutlined />,
  },
}

const CompanyDetailPage: React.FC<CompanyDetailPageProps> = ({ isCreateMode }) => {
  const { companyId } = useParams<{ companyId: string }>();
  const companiesContext = useContext(CompaniesContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') ?? TABS.generalInformation.key);

  const selectionMismatch =
    (!isCreateMode && companiesContext.selected === null) ||
    (!isCreateMode && companiesContext.selected?.id !== companyId);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  useEffect(() => {
    if (
      companyId &&
      selectionMismatch &&
      companiesContext.selectionStatus !== ContextStatus.LOADING
    ) {
      companiesContext.fetchAndSelectById(companyId);
    }
    return () => {
      // Reset context only in production (hotreload will reset if this if is removed)
      if (process.env.NODE_ENV !== 'development') {
        companiesContext.reset();
      }
    };
  }, [companyId]);

  useEffect(() => {
    if (companiesContext.selected?.id) {
      const promises = [
        companiesContext.fetchUsers(companiesContext.selected?.id),
        companiesContext.fetchSubscriptions(companiesContext.selected?.id),
      ];
      Promise.all(promises);
    }
  }, [companiesContext.selected]);

  useEffect(() => {
    if (companiesContext.statusAddRemoveUser === ContextStatus.SUCCESS) {
      message.success(<Flex align="center" gap={6}>Les utilisateurs de la compagnie ont été mis à jour<CheckCircleOutlined /></Flex>);
      if (companyId) {
        companiesContext.fetchUsers(companyId);
      }
    }

    if (companiesContext.statusAddRemoveUser === ContextStatus.ERROR) {
      message.error(companiesContext.errorMessage);
      if (companyId) {
        companiesContext.fetchUsers(companyId);
      }
    }
  }, [companiesContext.statusAddRemoveUser, companyId]);

  if (!isCreateMode && companiesContext.selectionStatus === ContextStatus.LOADING) {
    return (
      <PageScaffold
        title="Chargement..."
        subtitle=""
        breadcrumbItems={[
          { target: COMPANIES_PAGE_PATH, title: 'Entreprises' },
          { title: '...' },
        ]}
      >
        <Skeleton active />
      </PageScaffold>
    );
  }

  if (!isCreateMode && !companiesContext.selected) {
    return (
      <PageScaffold
        title="Entreprise introuvable"
        subtitle=""
        breadcrumbItems={[
          { target: COMPANIES_PAGE_PATH, title: 'Entreprises' },
          { title: 'Introuvable' },
        ]}
      >
        <Alert type="error" message="Cette entreprise n'existe pas ou a été supprimée." />
      </PageScaffold>
    );
  }

  return (
    <PageScaffold
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      title={isCreateMode ? "Nouvelle entreprise" : companiesContext.selected!.name} // ! is safe because we check if selected is not null 
      subtitle={isCreateMode ? "Ajoutez une nouvelle entreprise" : "Consultez et modifiez les informations de l'entreprise"}
      breadcrumbItems={[
        { target: COMPANIES_PAGE_PATH, title: 'Entreprises' },
        { title: isCreateMode ? "Nouvelle" : companiesContext.selected?.name || '' },
      ]}
    >
      {(() => {
        if (isCreateMode) return null;

        const subs = companiesContext.companySubscriptions ?? [];
        const cancelledSubs = subs.filter((s) => s.status === SubscriptionStatus.CANCELLED);
        const pausedSubs = subs.filter((s) => s.status === SubscriptionStatus.PAUSED);

        const formatSubLabel = (s: { id: string; name: string; sender: string[] }) =>
          s.name || s.sender?.[0] || s.id;

        return (
          <>
            {cancelledSubs.length > 0 && (
              <Alert
                message={`${cancelledSubs.length} abonnement${cancelledSubs.length > 1 ? 's' : ''} résilié${cancelledSubs.length > 1 ? 's' : ''}`}
                description={
                  <Flex vertical gap={4}>
                    <Typography.Text strong>Certaines actions peuvent être bloquées.</Typography.Text>
                    <List dataSource={cancelledSubs.map(formatSubLabel)} renderItem={(item) => <List.Item>{item}</List.Item>} />
                  </Flex>
                }
                type="error"
                style={{ marginTop: '10px', marginBottom: '10px' }}
                banner
                closable
              />
            )}
            {pausedSubs.length > 0 && (
              <Alert
                message={`${pausedSubs.length} abonnement${pausedSubs.length > 1 ? 's' : ''} suspendu${pausedSubs.length > 1 ? 's' : ''}`}
                description={
                  <Flex vertical gap={4}>
                    <Typography.Text strong>Certaines actions peuvent être bloquées.</Typography.Text>
                    <List dataSource={pausedSubs.map(formatSubLabel)} renderItem={(item) => <List.Item>{item}</List.Item>} />
                  </Flex>
                }
                type="warning"
                style={{ marginTop: '10px', marginBottom: '10px' }}
                banner
                closable
              />
            )}
          </>
        );
      })()}

      {isCreateMode ? (
        <CompanyCreationFormProvider>
          <CompanyCreationForm />
        </CompanyCreationFormProvider>
      ) : selectionMismatch || companiesContext.selected == null ? (
        <Card loading />
      ) : (
        <CompanyEditionFormProvider company={companiesContext.selected}>
          <Tabs defaultActiveKey={activeTab}
            activeKey={activeTab}
            onChange={handleTabChange}
            items={[
              {
                ...TABS.generalInformation,
                children: <CompanyDetails />
              },
              {
                ...TABS.users,
                children: <CompanyUsersList />,
              },
              {
                ...TABS.subscriptions,
                children: <CompanySubscriptionsList />,
              },
              {
                ...TABS.sms,
                children: <Alert message="A venir" type="info" />,
              },
              {
                ...TABS.costs,
                children: <CompanyCosts />,
              },
              {
                ...TABS.files,
                children: <CompanyFilesProvider>
                  <CompanyFilesList companyId={companiesContext.selected.id} />
                </CompanyFilesProvider>,
              },
            ]}
          />
        </CompanyEditionFormProvider>
      )}
    </PageScaffold>
  );
};

export default CompanyDetailPage;
